import clsx from "clsx";
import type { StaticImport } from "next/dist/shared/lib/get-img-props";
import NextImage, { ImageProps } from "next/image";
import React, { useState, useEffect, type JSX } from "react";

interface Props extends ImageProps {
  isBackgroundImage?: boolean;
  srcSet?: string;
  sizes?: string;
}

// Valid images' url start for NextImage
const VALID_IMAGE_URL_START = ["https://", "http://", "/", "blob:"];

const isInvalidImageSrc = (src: string | StaticImport, isBackgroundImage = false) =>
  !isBackgroundImage &&
  typeof src === "string" &&
  VALID_IMAGE_URL_START.every((validStart) => !src.startsWith(validStart));

export const ImageWithFallback = ({
  alt,
  src,
  isBackgroundImage,
  srcSet,
  sizes,
  onError,
  priority,
  ...props
}: Props): JSX.Element => {
  const [hasError, setHasError] = useState(isInvalidImageSrc(src, isBackgroundImage));

  const isImageValid = () =>
    new Promise((resolve) => {
      const image = new Image();
      image.onerror = () => resolve(false);
      image.src = src as string;
    });

  useEffect(() => {
    setHasError(isInvalidImageSrc(src, isBackgroundImage));
  }, [src]);

  if (isBackgroundImage) {
    isImageValid().then((isValid) => setHasError(!isValid));

    const imageStyle: React.CSSProperties = hasError
      ? {}
      : {
          backgroundImage: `url(${src})`,
        };

    return <div className={clsx({ "bg-base-input": hasError }, props.className)} style={imageStyle} />;
  }

  if (hasError || !src) {
    return <div {...props} className={clsx("bg-base-input", props.className)} />;
  }

  // Use native <img> when srcSet is provided (Next.js Image doesn't support custom srcSet)
  if (srcSet) {
    return (
      <img
        alt={alt}
        src={src as string}
        srcSet={srcSet}
        sizes={sizes}
        width={props.width}
        height={props.height}
        className={props.className}
        style={props.style}
        onLoad={props.onLoad}
        onError={(event) => {
          setHasError(true);
          onError?.(event);
        }}
      />
    );
  }

  return (
    <NextImage
      alt={alt}
      onError={(event) => {
        setHasError(true);
        onError?.(event);
      }}
      src={src}
      sizes={sizes}
      priority={priority}
      {...props}
    />
  );
};
